// ─────────────────────────────────────────────────────────────────────────────
// graphPalette — colour as DATA, and the graph's OWN source of truth.
//
// OWNER RULING (this build): the specification's palette governs the graph.
// This file is its own source of truth and it reads NOTHING from lib/agents.ts,
// ACCENTS, radarAgents.ts or REXY_CONSOLE — and touches none of them. Those
// remain the app's palette; this is the graph's. The two disagree deliberately:
// agents.ts gives PERSONAL, BUSINESS, RESEARCH and FRONTIER the same neutral
// #d4d4d4, while the graph gives FRONTIER its own gold.
//
// HOMEPAGE, BUSINESS AND RESEARCH SHARING ONE WHITE IS INTENTIONAL. They are
// told apart by stable spatial location, labels, parent relationships and
// topology — not by colour. Do not "fix" it by differentiating them.
//
// PERSONAL IS DELIBERATELY UNRESOLVED. It falls to the labelled neutral below
// and logs ONE development warning. No permanent Personal colour is invented
// here; the owner rules it when he sees the galaxy.
//
// Nothing in this file is baked into a shader or a switch statement. A new
// domain arrives as data carrying its own `colour` and needs no renderer change.
// ─────────────────────────────────────────────────────────────────────────────

import { RexyGraphColourRole } from './graphRegistry';
import { RexyTrustTier } from './graphTypes';
import { clamp, lerp } from './graphMath';

export type Rgb = [number, number, number];

/** Owner-supplied domain colours. Personal is absent BY RULING, not by omission. */
export const INITIAL_REXY_DOMAIN_COLOURS = {
  homepage: '#F5F7FA',
  trading: '#FF9D2E',
  business: '#F5F7FA',
  research: '#F5F7FA',
  growth: '#2CD878',
  frontier: '#FFD84A',
  code: '#2EA8FF',
  security: '#FF4D57',
} as const;

/** The labelled fallback. A domain wearing this has NO ruled colour yet. */
export const UNRESOLVED_DOMAIN_COLOUR = '#B8C4D0';

/** City Hall — white-hot cyan. The single brightest thing in the graph. */
export const CITY_HALL_COLOUR = '#DFFBFF';

/** What `system` nodes are mixed toward — a cool neutral, never another hue. */
const SYSTEM_MIX_TARGET: Rgb = [0.62, 0.86, 0.94];
const WHITE: Rgb = [1, 1, 1];

// Mix amounts, from the specification's ranges. Constants, so every agent in the
// graph is mixed identically and the eye can learn the tier by brightness.
const AGENT_MIX_TOWARD_WHITE = 0.14; // spec 10–18%
const SYSTEM_MIX_TOWARD_NEUTRAL = 0.28; // spec 20–35%
const LEAF_DESATURATION = 0.35; // files: lower saturation…
const LEAF_DIM = 0.82; // …and lower value

/** Base alpha per colour role. Trust multiplies this; it never replaces it. */
const ROLE_BASE_ALPHA: Record<RexyGraphColourRole, number> = {
  'city-hall': 1.0,
  domain: 0.94,
  agent: 0.86,
  system: 0.72,
  leaf: 0.55,
};

/**
 * Trust affects alpha (and, from G1, halo and selected-path priority). It never
 * replaces the domain colour, and LOW is never invisible — 0.52 is a floor with
 * a reason, not a fade-out.
 */
export const TRUST_ALPHA_MULTIPLIER: Record<RexyTrustTier, number> = {
  HIGHEST: 1.0,
  HIGHER: 0.9,
  HIGH: 0.8,
  MEDIUM: 0.67,
  LOW: 0.52,
};

export function hexToRgb(hex: string): Rgb {
  const h = hex.charAt(0) === '#' ? hex.slice(1) : hex;
  const full =
    h.length === 3 ? h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2) : h;
  const n = parseInt(full, 16);
  if (isNaN(n) || full.length !== 6) return hexToRgb(UNRESOLVED_DOMAIN_COLOUR);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function mixRgb(a: Rgb, b: Rgb, k: number): Rgb {
  return [lerp(a[0], b[0], k), lerp(a[1], b[1], k), lerp(a[2], b[2], k)];
}

/**
 * Reduce a node or domain id to its palette key: `lab:domain:trading` → `trading`,
 * `domain:code` → `code`, `TRADING` → `trading`. One function so the lab source
 * and the future live adapter key the palette identically.
 */
export function domainSlug(idOrSlug: string): string {
  const parts = idOrSlug.split(':');
  return (parts.length ? parts[parts.length - 1] : idOrSlug).trim().toLowerCase();
}

// ONE warning per unresolved domain, development only. A missing colour is a
// thing to rule, not a thing to crash on — and not a thing to shout about on
// every frame either.
const warnedDomains: { [slug: string]: true } = {};

export interface ResolvedDomainColour {
  hex: string;
  rgb: Rgb;
  /** false when the domain fell back to UNRESOLVED_DOMAIN_COLOUR. */
  resolved: boolean;
}

export function resolveDomainColour(idOrSlug: string, override?: string): ResolvedDomainColour {
  if (override) return { hex: override, rgb: hexToRgb(override), resolved: true };

  const slug = domainSlug(idOrSlug);
  const table = INITIAL_REXY_DOMAIN_COLOURS as Record<string, string>;
  const hex = table[slug];
  if (hex) return { hex, rgb: hexToRgb(hex), resolved: true };

  if (process.env.NODE_ENV !== 'production' && !warnedDomains[slug]) {
    warnedDomains[slug] = true;
    // eslint-disable-next-line no-console
    console.warn(
      `[graphPalette] no ruled colour for domain "${slug}" — rendering the labelled ` +
        `neutral ${UNRESOLVED_DOMAIN_COLOUR}. This is a placeholder awaiting an owner ruling, ` +
        `not a chosen colour.`,
    );
  }
  return { hex: UNRESOLVED_DOMAIN_COLOUR, rgb: hexToRgb(UNRESOLVED_DOMAIN_COLOUR), resolved: false };
}

/**
 * The colour a node renders at: its domain's colour, transformed by its role.
 * An explicit `node.colour` always wins — that is how a single agent overrides
 * its inherited domain colour through data alone.
 */
export function nodeColour(role: RexyGraphColourRole, domainRgb: Rgb, override?: string): Rgb {
  if (override) return hexToRgb(override);

  if (role === 'city-hall') return hexToRgb(CITY_HALL_COLOUR);
  if (role === 'domain') return domainRgb;
  if (role === 'agent') return mixRgb(domainRgb, WHITE, AGENT_MIX_TOWARD_WHITE);
  if (role === 'system') return mixRgb(domainRgb, SYSTEM_MIX_TARGET, SYSTEM_MIX_TOWARD_NEUTRAL);

  // leaf — desaturate toward its own luminance, then dim. Files stay recognisably
  // their domain's colour; they simply stop competing with the structure.
  const lum = domainRgb[0] * 0.2126 + domainRgb[1] * 0.7152 + domainRgb[2] * 0.0722;
  const grey: Rgb = [lum, lum, lum];
  const flat = mixRgb(domainRgb, grey, LEAF_DESATURATION);
  return [flat[0] * LEAF_DIM, flat[1] * LEAF_DIM, flat[2] * LEAF_DIM];
}

/**
 * The floor a VISIBLE node may not fall below, by kind.
 *
 * Trust still orders everything above the floor — a LOW record stays dimmer than
 * a HIGHEST one — but no node that passed the filters is allowed to resolve to
 * something the eye cannot find. The floor is applied only to nodes the filters
 * kept; it never resurrects something deliberately hidden.
 */
const MIN_VISIBLE_ALPHA: { [kind: string]: number } = {
  'city-hall': 0.75,
  domain: 0.62,
  agent: 0.48,
  'memory-cluster': 0.22, 'memory-system': 0.24,
  tool: 0.22, model: 0.22, database: 0.22, workflow: 0.22,
  service: 0.22, surface: 0.22, evidence: 0.22, 'city-hall-room': 0.22,
  'memory-record': 0.1, 'memory-file': 0.1, file: 0.1, document: 0.1,
  citation: 0.09, thread: 0.09, artifact: 0.09,
  unknown: 0.09,
};

export function minVisibleAlphaFor(kind: string): number {
  const floor = MIN_VISIBLE_ALPHA[kind];
  return floor === undefined ? 0.09 : floor;
}

export function nodeAlpha(role: RexyGraphColourRole, trust?: RexyTrustTier): number {
  const base = ROLE_BASE_ALPHA[role];
  const mult = trust ? TRUST_ALPHA_MULTIPLIER[trust] : 1;
  return clamp(base * mult, 0, 1);
}

/** The graph lab's own background. Not the app atmosphere — the graph supplies its own. */
export const GRAPH_BACKGROUND_HEX = '#02050B';
export const GRAPH_BACKGROUND_INT = 0x02050b;
